import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Row } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../index";
import {
  closeAddressVersion,
  getAddressVersions,
  getIndividualInfoById,
  updateAddressVersions,
} from "../../../../store/slices/accountsTs";
import { checkIsLoaded, checkIsLoading } from "../../../../lib/utils";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import {
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils/formatDate";
import ToggleableVersionsTable from "../../../ToggleableVersionsTable/ToggleableVersionsTable";
import ChangeVersionsTable from "../../../common/ChangeVersionsTable";
import { TableRecord } from "../../../../lib/interfaces";

export const AccountContractsDetailsAddress = () => {
  const history = useHistory();
  const [addressId, setAddressId] = useState(null);
  const [filterId, setFilterId] = useState(0);
  const individualId = history.location.search.substring(1).split("=")[1];
  const {
    getIndividualByIdData,
    getIndividualByIdLoading,
    getAddressVersionsData,
    getAddressVersionsLoading,
  } = useAppSelector((state) => state.accountsTs);

  const { countries, regions } = useAppSelector((state) => state.dicts);

  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const [form] = Form.useForm();
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);
  const dispatch = useAppDispatch();

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    countryCode: "15%",
    regionId: "15%",
    countryId: "15%",
    unstructuredAddress: "25%",
    period: "15%",
    actions: "15%",
  };

  useEffect(() => {
    const version = childTableData.find(
      (item) => item.isCreated || item.isEditing || item.isClosing
    );
    if (version) {
      setFilterId(version.regionId);
      form.setFieldsValue({ regionId: version?.regionId, countryId: version?.countryId });
      if (version.isCreated) {
        form.setFieldsValue({ regionId: "", countryId: "" });
      }
    }
  }, [isChangingForbidden]);

  useEffect(() => {
    if (checkIsLoaded(getIndividualByIdLoading)) {
      const addressTypeNameCheck = Object.keys(getIndividualByIdData).find(
        (item) => item === "actualAddress"
      );
      if (countries && regions) {
        setTableData([
          {
            key: getIndividualByIdData?.actualAddress?.id,
            id: getIndividualByIdData?.actualAddress?.id,
            fromDate: getIndividualByIdData?.actualAddress?.fromDate,
            toDate: getIndividualByIdData?.actualAddress?.toDate,
            countryId: countries.find(
              (item) => item.id === getIndividualByIdData?.actualAddress?.countryId
            )?.name,
            regionId: regions.find(
              (item) => item.id === getIndividualByIdData?.actualAddress?.regionId
            )?.name,
            unstructuredAddress: getIndividualByIdData?.actualAddress?.unstructuredAddress,
            addressType: addressTypeNameCheck,
          },
        ]);
      }
    }
  }, [getIndividualByIdData, regions, countries]);

  useEffect(() => {
    if (checkIsLoaded(getAddressVersionsLoading)) {
      const addressTypeNameCheck = Object.keys(getIndividualByIdData).find(
        (item) => item === "actualAddress"
      );
      setChildTableData(
        getAddressVersionsData
          .map((item, i) => {
            return {
              id: item.id,
              fromDate: item.fromDate,
              toDate: item.toDate,
              regionId: item.regionId,
              countryId: item.countryId,
              unstructuredAddress: item.unstructuredAddress,
              addressType: addressTypeNameCheck,
              ...versionsTableUtils.getCommonChildTableProps(
                item,
                i,
                getAddressVersionsData.length
              ),
            };
          })
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [getAddressVersionsData]);

  const handleFilterCountries = (id) => {
    setFilterId(id);
    form.setFieldsValue({ regionId: id });
    form.setFieldsValue({ countryId: "" });
  };

  const [columns, setColumns] = useState([
    {
      title: "Тип адреса",
      dataIndex: "countryCode",
      key: "countryCode",
      width: columnsWidths.countryCode,
      hidden: false,
      render: (v, record) => {
        if (record.addressType === "actualAddress") {
          return "Фактический адрес";
        }
        return "Адрес регистрации";
      },
    },
    {
      title: "Регион",
      dataIndex: "regionId",
      key: "regionId",
      width: columnsWidths.regionId,
    },
    {
      title: "Страна",
      dataIndex: "countryId",
      key: "countryId",
      width: columnsWidths.countryId,
    },
    {
      title: "Адрес",
      dataIndex: "unstructuredAddress",
      key: "unstructuredAddress",
      width: columnsWidths.unstructuredAddress,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Тип адреса",
      dataIndex: "countryCode",
      key: "countryCode",
      width: columnsWidths.countryCode,
      hidden: false,
      render: (v, record) => {
        if (record.addressType === "actualAddress") {
          return "Фактический адрес";
        }
        return "Адрес регистрации";
      },
    },
    {
      title: "Регион",
      dataIndex: "regionId",
      key: "regionId",
      width: columnsWidths.regionId,
      render: (value, record) => (
        <ChangeVersionsTable.SelectFilter
          dic={regions}
          name="regionId"
          record={record}
          width="100%"
          onChange={handleFilterCountries}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Страна",
      dataIndex: "countryId",
      key: "countryId",
      width: columnsWidths.countryId,
      render: (value, record) => (
        <ChangeVersionsTable.SelectFilter
          dic={countries}
          name="countryId"
          record={record}
          width="100%"
          filterBy={filterId}
          filterItem="parentDicId"
          required
          setFieldsValue={form.setFieldsValue}
          disabled={!form.getFieldValue("regionId")}
        />
      ),
    },
    {
      title: "Адрес",
      dataIndex: "unstructuredAddress",
      key: "unstructuredAddress",
      width: columnsWidths.unstructuredAddress,
      render: (value, record) => (
        <ChangeVersionsTable.Input
          name="unstructuredAddress"
          record={record}
          setFieldsValue={form.setFieldsValue}
        />
      ),
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (v, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
          disabledToDate={(current) =>
            current.isBefore(record.fromDate, "day") || current.isAfter(record.toDate, "day")
          }
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        //  const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              // TODO пока захардкожены hasAccess=true
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
              newSelectFieldValueToReset="regionId"
            />
            <ChangeVersionsTable.EditButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden, addressId, filterId]);

  const onFinish = (values) => {
    const version = childTableData.find(
      (item) => item.isCreated || item.isEditing || item.isClosing
    );
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);

    if (version.isCreated || version.isEditing) {
      const isForceUpdate = Boolean(changeableRecord.isEditing);
      const fromDate = isForceUpdate
        ? changeableRecord.fromDate
        : toHttpFormatStartOfDay(values.fromDate);

      dispatch(
        updateAddressVersions({
          addressId: changeableRecord?.id,
          fromDate,
          force: isForceUpdate,
          body: {
            countryId: values.countryId,
            regionId: values.regionId,
            unstructuredAddress: values.unstructuredAddress,
            postCode: " ",
          },
        })
      ).then(({ payload }) => {
        if (payload) {
          dispatch(getIndividualInfoById({ individualId }));
          dispatch(getAddressVersions(Number(addressId)));
        }
      });
    }

    if (version.isClosing) {
      dispatch(
        closeAddressVersion({
          addressId: changeableRecord?.id,
          fromDate: toHttpFormat(values.toDate),
        })
      ).then(() => {
        dispatch(getIndividualInfoById({ individualId }));
        dispatch(getAddressVersions(Number(addressId)));
      });
    }
  };
  return (
    <div className="account-details-table-wrapper">
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          title={
            <Row align="middle">
              <h4 style={{ margin: 0 }}>Адрес</h4>
            </Row>
          }
          tableName="account-details-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          childTableData={childTableData}
          isToggleableColumns={false}
          isChildTableLoading={checkIsLoading(getAddressVersionsLoading)}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded) {
              dispatch(getAddressVersions(record?.id));
              setAddressId(record?.id);
            }
          }}
          isPageable={false}
        />
      </Form>
    </div>
  );
};
